import { useState, useEffect } from 'react';
import cadenceGqlCall from '../utils/cadence-gql-call';
import { CADENCE_TEMPLATE_INSTANCES_QUERY } from '../utils/cadence-queries';

/**
 * Fetch every cadence created from a blueprint, regardless of the current
 * viewer's permission on each individual cadence.
 *
 * Unlike useCadences (which drops anything the viewer can't open), this is
 * meant for the blueprint detail page, where the count/list must be accurate
 * for anyone who can see the blueprint — canView/canEdit on each returned
 * cadence tells the caller whether that specific row is clickable.
 *
 * @param {string|number} templateId
 * @returns {{ instances, loading, error, refetch }}
 */
const useTemplateInstances = (templateId) => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(!!templateId);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    if (!templateId) {
      setInstances([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    cadenceGqlCall(CADENCE_TEMPLATE_INSTANCES_QUERY, { templateId })
      .then((data) => {
        if (cancelled) return;
        const nodes = data.cadenceTemplateInstances || [];
        setInstances(nodes.map((c) => ({ ...c, cadenceType: c.cadenceType?.toLowerCase() || c.cadenceType })));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to fetch cadences created from this blueprint');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [templateId, fetchKey]);

  const refetch = () => setFetchKey((k) => k + 1);

  return { instances, loading, error, refetch };
};

export default useTemplateInstances;
